import { useEffect, useLayoutEffect, useMemo, useRef, useState, type FormEvent } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { useQueryClient } from "@tanstack/react-query";
import { Badge, Button, Dialog, EmptyState, Field, Icon, IconButton, Input, useLayout, useToast } from "@agentsaas/ui";
import { ApiError, type Actor, type Agent, type Conversation, type Message } from "@agentsaas/protocol";
import { useErrorText, useT } from "../app/i18n";
import { useServices } from "../app/services";
import { useSession } from "../app/session";
import { formatMicroUsd, formatTime } from "../app/format";
import { useAgents } from "../data/queries";
import { putConversation, useConversation, useRoomStream, useRooms } from "../data/rooms";
import { AgentStatusDot } from "../agent/TeamList";
import { ProjectChrome, ProjectNav } from "../project/ProjectChrome";
import { MobileFrame } from "../mobile/MobileProject";
import { Loading } from "../layout/Loading";
import { RoomComposer, type ComposerSend } from "./Composer";
import { MeetingList } from "../meetings/MeetingsPage";
import { budgetRatio, memberAgents, parseAssetRef, refLabel, refsOfParts, roomTitle, sameActor, splitMentions, textOfParts, type GuardReason } from "./model";
import { AssetRefList, collectAssetRefs } from "../assets/AssetRef";
import { stripRefLines } from "../assets/refs";

/**
 * /p/:pid/rooms[/:cid]：聊天室。
 * - 桌面 / 平板：项目导航 + 房间列表 | 房间（消息流 + 输入框），≥1280 右侧显示成员与护栏
 * - 手机：底部 tab"对话"= 会话中心（房间 + agent 私聊）；进入房间是全屏下钻
 */
export function RoomsPage() {
  const { pid = "", cid } = useParams();
  const layout = useLayout();
  if (layout === "mobile")
    return (
      <>
        <ProjectChrome pid={pid} />
        <MobileFrame pid={pid} active="chat">
          {cid ? <RoomView key={cid} pid={pid} cid={cid} mobile /> : <ConversationsHub pid={pid} />}
        </MobileFrame>
      </>
    );
  return (
    <>
      <ProjectChrome pid={pid} />
      <div className="pframe">
        <ProjectNav pid={pid} active="rooms" />
        <div className="pframe__body rooms">
          <aside className="rooms__list">
            <RoomList pid={pid} activeId={cid} />
          </aside>
          <section className="rooms__main">
            {cid ? (
              <RoomView key={cid} pid={pid} cid={cid} wide={layout === "desktop"} />
            ) : (
              <PickRoom />
            )}
          </section>
        </div>
      </div>
    </>
  );
}

function PickRoom() {
  const { t } = useT();
  return <EmptyState icon="chat" title={t("room.pick")} />;
}

// ───────────── 房间列表 / 会话中心 ─────────────

function RoomList({ pid, activeId }: { pid: string; activeId?: string }) {
  const { t } = useT();
  const rooms = useRooms(pid);
  const agents = useAgents(pid).data ?? [];
  const [creating, setCreating] = useState(false);
  return (
    <div className="room-list">
      <div className="room-list__head">
        <h2 className="section-title grow">{t("room.list_title")}</h2>
        <Button size="sm" icon="plus" onClick={() => setCreating(true)} data-testid="room-new">
          {t("room.new")}
        </Button>
      </div>
      {rooms.isPending ? (
        <Loading />
      ) : rooms.data?.length ? (
        <ul className="list" data-testid="room-list">
          {rooms.data.map((c) => (
            <li key={c.id}>
              <Link to={`/p/${pid}/rooms/${c.id}`} className={`list-item room-item${c.id === activeId ? " is-active" : ""}`} aria-current={c.id === activeId ? "page" : undefined} data-testid="room-item">
                <Icon name="chat" />
                <span className="grow room-item__main">
                  <span className="ellipsis room-item__title">{roomTitle(c, agents)}</span>
                  <span className="muted small">{t("room.member_count", { count: c.members.length })}</span>
                </span>
                {c.muted ? <Icon name="bellOff" size={16} aria-label={t("room.muted")} /> : null}
                {c.paused ? <Badge tone="warning">{t("room.paused")}</Badge> : null}
              </Link>
            </li>
          ))}
        </ul>
      ) : (
        <EmptyState icon="chat" title={t("room.empty_list")}>
          <p className="muted small">{t("room.empty_list_hint")}</p>
        </EmptyState>
      )}
      <NewRoomDialog pid={pid} open={creating} onClose={() => setCreating(false)} />
    </div>
  );
}

/** 手机"对话" tab：聊天室 + agent 私聊 */
export function ConversationsHub({ pid }: { pid: string }) {
  const { t } = useT();
  const agents = useAgents(pid).data ?? [];
  return (
    <div className="page stack hub" data-testid="conversations-hub">
      <RoomList pid={pid} />
      <MeetingList pid={pid} compact />
      <section className="stack">
        <h2 className="section-title">{t("room.dms")}</h2>
        {agents.length ? (
          <ul className="list" data-testid="dm-list">
            {agents.map((a) => (
              <li key={a.id}>
                <Link to={`/p/${pid}/a/${a.id}?tab=chat`} className="list-item" data-testid="dm-item">
                  <AgentStatusDot status={a.status} />
                  <span className="grow ellipsis">{a.name}</span>
                  <span className="muted small">{a.role}</span>
                  <Icon name="chevronRight" />
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <p className="muted small">{t("mobile.pick_agent")}</p>
        )}
      </section>
    </div>
  );
}

// ───────────── 房间 ─────────────

function useActorName(pid: string) {
  const { t } = useT();
  const me = useSession().state.user;
  const agents = useAgents(pid).data ?? [];
  return (a: Actor) => {
    if (a.type === "system") return t("room.system");
    if (a.type === "user") return me && a.id === me.id ? t("chat.you") : t("room.member_user");
    return agents.find((x) => x.id === a.id)?.name ?? a.id;
  };
}

export function RoomView({ pid, cid, mobile, wide }: { pid: string; cid: string; mobile?: boolean; wide?: boolean }) {
  const { t, locale } = useT();
  const errText = useErrorText();
  const toast = useToast();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const { api } = useServices();
  const session = useSession();
  const me = session.state.user;
  const tz = me?.timezone;
  const conv = useConversation(cid);
  const agentsData = useAgents(pid).data;
  const agents = useMemo(() => agentsData ?? [], [agentsData]);
  const stream = useRoomStream(cid);
  const nameOf = useActorName(pid);
  const [replyTo, setReplyTo] = useState<Message | null>(null);
  const [dialog, setDialog] = useState<"members" | "settings" | null>(null);
  const [guardBusy, setGuardBusy] = useState(false);
  const logRef = useRef<HTMLDivElement>(null);
  const stick = useRef(true);

  const members = useMemo(() => (conv.data ? memberAgents(conv.data, agents) : []), [conv.data, agents]);
  const names = agents.map((a) => a.name);
  const byId = useMemo(() => new Map(stream.messages.map((m) => [m.id, m])), [stream.messages]);

  // 新消息时如果本来就在底部，保持贴底
  useLayoutEffect(() => {
    const el = logRef.current;
    if (el && stick.current) el.scrollTop = el.scrollHeight;
  }, [stream.messages.length, stream.typing.length]);
  const onScroll = () => {
    const el = logRef.current;
    if (el) stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
  };
  useEffect(() => {
    stick.current = true;
  }, [cid]);

  if (conv.isPending) return <Loading />;
  if (!conv.data) return <EmptyState icon="chat" title={t("error.common.not_found")} />;
  const c = conv.data;
  const title = roomTitle(c, agents);

  const send = async (v: ComposerSend): Promise<boolean> => {
    try {
      const msg = await api.call("messagePost", { params: { cid }, body: { parts: v.parts, mentions: v.mentions, ...(v.replyTo ? { replyTo: v.replyTo } : {}) } });
      stream.addLocal(msg);
      setReplyTo(null);
      stick.current = true;
      return true;
    } catch (e) {
      toast.show({ message: errText(e), tone: "danger" });
      if (e instanceof ApiError && e.code === "conversation.paused") putConversation(qc, { ...c, paused: true });
      return false;
    }
  };

  const onGuard = async (op: "pause" | "resume") => {
    setGuardBusy(true);
    try {
      const next = await api.call("conversationGuard", { params: { cid, op } });
      putConversation(qc, next);
      if (op === "resume") stream.setPausedReason(null);
      else stream.setPausedReason("manual");
      toast.show({ message: t(op === "resume" ? "room.resumed" : "room.paused"), tone: "success", duration: 1500 });
    } catch (e) {
      toast.show({ message: errText(e), tone: "danger" });
    } finally {
      setGuardBusy(false);
    }
  };

  const typingNames = stream.typing.map((id) => agents.find((a) => a.id === id)?.name ?? id);

  const header = (
    <div className={mobile ? "drill-head room-head" : "pane-head room-head"}>
      {mobile ? <IconButton icon="back" label={t("common.back")} onClick={() => navigate(`/p/${pid}/rooms`)} data-testid="drill-back" /> : <Icon name="chat" />}
      <strong className="grow ellipsis" data-testid="room-title">
        {title}
      </strong>
      {c.muted ? <Icon name="bellOff" size={16} aria-label={t("room.muted")} /> : null}
      <Button size="sm" variant="ghost" icon="users" onClick={() => setDialog("members")} aria-label={t("room.manage_members")} data-testid="room-members">
        {c.members.length}
      </Button>
      {c.paused ? null : (
        <IconButton icon="pause" label={t("room.pause")} onClick={() => void onGuard("pause")} disabled={guardBusy} data-testid="room-pause" />
      )}
      <IconButton icon="settings" label={t("room.settings")} onClick={() => setDialog("settings")} data-testid="room-settings" />
    </div>
  );

  return (
    <div className={`room${wide ? " room--wide" : ""}${mobile ? " room--mobile" : ""}`} data-testid="room">
      <div className="room__main">
        {header}
        {c.paused ? <GuardBanner reason={stream.pausedReason} busy={guardBusy} onResume={() => void onGuard("resume")} /> : null}
        {!wide ? <BudgetBar conv={c} compact /> : null}
        <div className="room__log" ref={logRef} onScroll={onScroll} role="log" aria-live="polite" aria-label={title} data-testid="room-log">
          {stream.hasOlder ? (
            <Button size="sm" variant="ghost" onClick={() => void stream.loadOlder()} loading={stream.loadingOlder} className="room__older">
              {t("room.load_older")}
            </Button>
          ) : null}
          {!stream.loaded ? <div className="chat__status muted small">{t("chat.history_loading")}</div> : null}
          {stream.loaded && !stream.messages.length ? <div className="chat__status muted">{t("room.empty")}</div> : null}
          {stream.messages.map((m) => (
            <RoomMessage
              key={m.id}
              pid={pid}
              msg={m}
              name={nameOf(m.from)}
              mine={!!me && m.from.type === "user" && m.from.id === me.id}
              quoted={m.replyTo ? (byId.get(m.replyTo) ?? null) : null}
              quotedName={m.replyTo && byId.get(m.replyTo) ? nameOf(byId.get(m.replyTo)!.from) : ""}
              known={names}
              time={formatTime(m.createdAt, locale, tz)}
              onReply={() => setReplyTo(m)}
            />
          ))}
          {typingNames.length ? (
            <div className="room__typing small muted" data-testid="typing">
              <span className="typing-dots" aria-hidden="true">
                <i />
                <i />
                <i />
              </span>
              {t("room.typing", { names: typingNames.join(", "), count: typingNames.length })}
            </div>
          ) : null}
        </div>
        <RoomComposer
          pid={pid}
          members={members}
          replyTo={replyTo}
          replyName={replyTo ? nameOf(replyTo.from) : ""}
          onCancelReply={() => setReplyTo(null)}
          disabled={c.paused}
          disabledHint={t("room.paused_hint")}
          onSend={send}
        />
      </div>
      {wide ? (
        <aside className="room__aside" aria-label={t("room.members")}>
          <BudgetBar conv={c} />
          <MemberList pid={pid} conv={c} agents={agents} />
        </aside>
      ) : null}
      <MembersDialog conv={c} agents={agents} open={dialog === "members"} onClose={() => setDialog(null)} />
      <RoomSettingsDialog conv={c} open={dialog === "settings"} onClose={() => setDialog(null)} />
    </div>
  );
}

function GuardBanner({ reason, busy, onResume }: { reason: GuardReason | null; busy: boolean; onResume(): void }) {
  const { t } = useT();
  return (
    <div className="banner banner--warning room__guard" role="alert" data-testid="guard-banner" data-reason={reason ?? "unknown"}>
      <Icon name="shield" />
      <div className="grow">
        <strong>{t("room.paused")}</strong>
        <div className="small">{t(`room.paused.${reason ?? "manual"}`)}</div>
      </div>
      <Button variant="primary" size="sm" icon="play" onClick={onResume} loading={busy} data-testid="guard-resume">
        {t("room.resume")}
      </Button>
    </div>
  );
}

export function BudgetBar({ conv, compact }: { conv: Conversation; compact?: boolean }) {
  const { t, locale } = useT();
  const ratio = budgetRatio(conv);
  const used = formatMicroUsd(conv.usedMicroUsd, locale);
  if (ratio === null)
    return compact ? null : (
      <div className="budget small muted" data-testid="room-budget">
        {t("room.budget_unlimited", { used })}
      </div>
    );
  const label = t("room.budget_usage", { used, budget: formatMicroUsd(conv.budgetMicroUsd!, locale) });
  const tone = ratio >= 1 ? "danger" : ratio >= 0.8 ? "warning" : "ok";
  return (
    <div className={`budget${compact ? " budget--compact" : ""}`} data-testid="room-budget">
      <div className="budget__label small">
        <span className="grow">{t("room.budget")}</span>
        <span className={tone === "ok" ? "muted" : `budget__text--${tone}`}>{label}</span>
      </div>
      <div className="budget__track" role="meter" aria-label={t("room.budget")} aria-valuemin={0} aria-valuemax={100} aria-valuenow={Math.round(ratio * 100)} aria-valuetext={label}>
        <div className={`budget__fill budget__fill--${tone}`} style={{ width: `${Math.round(ratio * 100)}%` }} />
      </div>
    </div>
  );
}

function RoomMessage({
  pid,
  msg,
  name,
  mine,
  quoted,
  quotedName,
  known,
  time,
  onReply,
}: {
  pid: string;
  msg: Message;
  name: string;
  mine: boolean;
  quoted: Message | null;
  quotedName: string;
  known: string[];
  time: string;
  onReply(): void;
}) {
  const { t } = useT();
  const text = textOfParts(msg.parts);
  const refs = refsOfParts(msg.parts);
  // 资产引用（附件 + 正文里出现的）直接预览成卡片；其他引用仍是标签
  const assetRefs = collectAssetRefs(
    refs.map((r) => r.ref),
    text,
  );
  const otherRefs = refs.filter((r) => !parseAssetRef(r.ref));
  const jump = () => {
    const el = document.getElementById(`m-${msg.replyTo}`);
    el?.scrollIntoView({ block: "center", behavior: "smooth" });
    el?.classList.add("is-flash");
    setTimeout(() => el?.classList.remove("is-flash"), 1200);
  };
  return (
    <article id={`m-${msg.id}`} className={`rmsg rmsg--${msg.from.type}${mine ? " rmsg--mine" : ""}`} data-testid="room-msg" data-from={name} aria-label={t("room.message_from", { name })}>
      <header className="rmsg__head small">
        {msg.from.type === "agent" ? <Icon name="user" size={14} /> : null}
        <strong>{name}</strong>
        <time className="muted" dateTime={msg.createdAt}>
          {time}
        </time>
        <span className="grow" />
        <IconButton icon="reply" label={t("room.reply")} onClick={onReply} className="rmsg__reply" data-testid="msg-reply" />
      </header>
      {msg.replyTo ? (
        quoted ? (
          <button type="button" className="rmsg__quote small" onClick={jump} aria-label={t("room.jump_to")} data-testid="msg-quote">
            <Icon name="reply" size={12} />
            <strong>{quotedName}</strong>
            <span className="ellipsis muted">{textOfParts(quoted.parts)}</span>
          </button>
        ) : (
          <div className="rmsg__quote small muted">{t("room.quote_missing")}</div>
        )
      ) : null}
      {stripRefLines(text) ? (
        <p className="rmsg__text">
          {splitMentions(stripRefLines(text), known).map((s, i) =>
            s.mention ? (
              <span key={i} className="mention">
                {s.text}
              </span>
            ) : (
              <span key={i}>{s.text}</span>
            ),
          )}
        </p>
      ) : null}
      {otherRefs.length ? (
        <div className="refs rmsg__refs">
          {otherRefs.map((r) => (
            <span key={r.ref} className="chip" title={r.ref} data-testid="msg-ref">
              <Icon name="paperclip" size={14} />
              <span className="ellipsis">{refLabel(r)}</span>
            </span>
          ))}
        </div>
      ) : null}
      <AssetRefList pid={pid} refs={assetRefs} names={Object.fromEntries(refs.map((r) => [r.ref, r.name]))} />
    </article>
  );
}

// ───────────── 成员 ─────────────

function MemberList({ pid, conv, agents }: { pid: string; conv: Conversation; agents: Agent[] }) {
  const { t } = useT();
  const inRoom = memberAgents(conv, agents);
  return (
    <section className="stack">
      <h3 className="section-title">
        {t("room.members")} <Badge>{conv.members.length}</Badge>
      </h3>
      <ul className="list" data-testid="room-member-list">
        <li className="list-item">
          <Icon name="user" />
          <span className="grow">{t("chat.you")}</span>
        </li>
        {inRoom.map((a) => (
          <li key={a.id}>
            <Link to={`/p/${pid}/a/${a.id}`} className="list-item">
              <AgentStatusDot status={a.status} />
              <span className="grow ellipsis">{a.name}</span>
              <span className="muted small">{a.role}</span>
            </Link>
          </li>
        ))}
      </ul>
    </section>
  );
}

function useSaveConv() {
  const qc = useQueryClient();
  const toast = useToast();
  const errText = useErrorText();
  return async (fn: () => Promise<Conversation>) => {
    try {
      const next = await fn();
      putConversation(qc, next);
      return true;
    } catch (e) {
      toast.show({ message: errText(e), tone: "danger" });
      return false;
    }
  };
}

function MembersDialog({ conv, agents, open, onClose }: { conv: Conversation; agents: Agent[]; open: boolean; onClose(): void }) {
  const { t } = useT();
  const { api } = useServices();
  const save = useSaveConv();
  const [busy, setBusy] = useState<string | null>(null);
  const isIn = (a: Agent) => conv.members.some((m) => sameActor(m, { type: "agent", id: a.id }));
  const change = async (a: Agent, add: boolean) => {
    setBusy(a.id);
    const actor = { type: "agent" as const, id: a.id };
    await save(() => api.call("conversationMembers", { params: { cid: conv.id }, body: add ? { add: [actor], remove: [] } : { add: [], remove: [actor] } }));
    setBusy(null);
  };
  return (
    <Dialog open={open} onClose={onClose} title={t("room.manage_members")} closeLabel={t("shell.close")}>
      <ul className="list" data-testid="members-dialog">
        <li className="list-item">
          <Icon name="user" />
          <span className="grow">{t("chat.you")}</span>
        </li>
        {agents.map((a) => {
          const inside = isIn(a);
          return (
            <li key={a.id} className="list-item" data-testid="member-row">
              <AgentStatusDot status={a.status} />
              <span className="grow ellipsis">{a.name}</span>
              {inside ? (
                <Button size="sm" variant="ghost" loading={busy === a.id} onClick={() => void change(a, false)} aria-label={t("room.member_remove", { name: a.name })} data-testid={`member-remove-${a.name}`}>
                  {t("room.member_remove_short")}
                </Button>
              ) : (
                <Button size="sm" icon="plus" loading={busy === a.id} onClick={() => void change(a, true)} aria-label={t("room.member_add", { name: a.name })} data-testid={`member-add-${a.name}`}>
                  {t("room.member_add_short")}
                </Button>
              )}
            </li>
          );
        })}
      </ul>
    </Dialog>
  );
}

const toMicro = (usd: string) => (usd.trim() === "" ? null : Math.round(Number(usd) * 1_000_000));
const fromMicro = (m: number | null) => (m === null ? "" : String(m / 1_000_000));

function GuardFields({ hop, setHop, budget, setBudget }: { hop: string; setHop(v: string): void; budget: string; setBudget(v: string): void }) {
  const { t } = useT();
  return (
    <>
      <Field label={t("room.hop_limit")} hint={t("room.hop_limit_hint")}>
        {(id, d) => <Input id={id} aria-describedby={d} type="number" min={1} max={20} inputMode="numeric" value={hop} onChange={(e) => setHop(e.target.value)} data-testid="room-hop" />}
      </Field>
      <Field label={t("room.budget_field")} hint={t("room.budget_hint")}>
        {(id, d) => <Input id={id} aria-describedby={d} type="number" min={0} step="0.01" inputMode="decimal" value={budget} onChange={(e) => setBudget(e.target.value)} data-testid="room-budget-input" />}
      </Field>
    </>
  );
}

function validGuard(hop: string, budget: string) {
  const h = Number(hop);
  const b = toMicro(budget);
  return Number.isInteger(h) && h >= 1 && h <= 20 && (b === null || (Number.isFinite(b) && b >= 0));
}

export function NewRoomDialog({ pid, open, onClose }: { pid: string; open: boolean; onClose(): void }) {
  const { t } = useT();
  const { api } = useServices();
  const qc = useQueryClient();
  const toast = useToast();
  const errText = useErrorText();
  const navigate = useNavigate();
  const agents = useAgents(pid).data ?? [];
  const [title, setTitle] = useState("");
  const [picked, setPicked] = useState<string[]>([]);
  const [hop, setHop] = useState("6");
  const [budget, setBudget] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>();
  useEffect(() => {
    if (!open) return;
    setTitle("");
    setPicked([]);
    setHop("6");
    setBudget("");
    setError(undefined);
  }, [open]);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!picked.length) return setError(t("room.pick_members_error"));
    if (!validGuard(hop, budget)) return;
    setBusy(true);
    try {
      const conv = await api.call("conversationCreate", {
        params: { pid },
        body: { kind: "room", ...(title.trim() ? { title: title.trim() } : {}), members: picked.map((id) => ({ type: "agent" as const, id: id as Agent["id"] })), hopLimit: Number(hop), budgetMicroUsd: toMicro(budget) },
      });
      putConversation(qc, conv);
      toast.show({ message: t("room.created"), tone: "success", duration: 1500 });
      onClose();
      navigate(`/p/${pid}/rooms/${conv.id}`);
    } catch (err) {
      toast.show({ message: errText(err), tone: "danger" });
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open={open} onClose={onClose} title={t("room.new")} closeLabel={t("shell.close")}>
      <form className="stack" onSubmit={(e) => void submit(e)} data-testid="room-form">
        <Field label={t("room.title_field")}>{(id) => <Input id={id} value={title} maxLength={100} onChange={(e) => setTitle(e.target.value)} data-testid="room-title-input" data-autofocus />}</Field>
        <fieldset className="plain stack">
          <legend className="ui-field__label">{t("room.members_field")}</legend>
          {agents.length ? (
            agents.map((a) => (
              <label key={a.id} className="check">
                <input
                  type="checkbox"
                  checked={picked.includes(a.id)}
                  onChange={(e) => {
                    setError(undefined);
                    setPicked((xs) => (e.target.checked ? [...xs, a.id] : xs.filter((x) => x !== a.id)));
                  }}
                  data-testid={`room-member-${a.name}`}
                />
                <AgentStatusDot status={a.status} />
                <span className="grow">{a.name}</span>
                <span className="muted small">{a.role}</span>
              </label>
            ))
          ) : (
            <p className="muted small">{t("room.no_agents")}</p>
          )}
          {error ? (
            <div className="form-error" role="alert">
              {error}
            </div>
          ) : null}
        </fieldset>
        <GuardFields hop={hop} setHop={setHop} budget={budget} setBudget={setBudget} />
        <Button type="submit" variant="primary" loading={busy} disabled={!validGuard(hop, budget)} data-testid="room-submit">
          {t("common.create")}
        </Button>
      </form>
    </Dialog>
  );
}

function RoomSettingsDialog({ conv, open, onClose }: { conv: Conversation; open: boolean; onClose(): void }) {
  const { t } = useT();
  const { api } = useServices();
  const save = useSaveConv();
  const toast = useToast();
  const [title, setTitle] = useState(conv.title ?? "");
  const [hop, setHop] = useState(String(conv.hopLimit));
  const [budget, setBudget] = useState(fromMicro(conv.budgetMicroUsd));
  const [muted, setMuted] = useState(conv.muted);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (!open) return;
    setTitle(conv.title ?? "");
    setHop(String(conv.hopLimit));
    setBudget(fromMicro(conv.budgetMicroUsd));
    setMuted(conv.muted);
  }, [open, conv]);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validGuard(hop, budget)) return;
    setBusy(true);
    const ok = await save(() => api.call("conversationPatch", { params: { cid: conv.id }, body: { title: title.trim(), hopLimit: Number(hop), budgetMicroUsd: toMicro(budget), muted } }));
    setBusy(false);
    if (ok) {
      toast.show({ message: t("room.saved"), tone: "success", duration: 1500 });
      onClose();
    }
  };
  return (
    <Dialog open={open} onClose={onClose} title={t("room.settings")} closeLabel={t("shell.close")}>
      <form className="stack" onSubmit={(e) => void submit(e)} data-testid="room-settings-form">
        <Field label={t("room.title_field")}>{(id) => <Input id={id} value={title} maxLength={100} onChange={(e) => setTitle(e.target.value)} data-testid="room-settings-title" />}</Field>
        <GuardFields hop={hop} setHop={setHop} budget={budget} setBudget={setBudget} />
        <label className="check">
          <input type="checkbox" checked={muted} onChange={(e) => setMuted(e.target.checked)} data-testid="room-muted" />
          <span>{t("room.mute")}</span>
        </label>
        <Button type="submit" variant="primary" loading={busy} disabled={!validGuard(hop, budget)} data-testid="room-settings-save">
          {t("common.save")}
        </Button>
      </form>
    </Dialog>
  );
}

